import { RuleDataForm, RuleItem, RuleStatuses, RuleTransitions, RuleTriggers } from 'models/rules';
import { camelCase, get, omit } from 'lodash';
import { queryKeys, sdk, API_FUNCTIONS, checkContainsId, checkIfGreatSuccess, RULES_ENDPOINTS } from 'utils/query';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { useDrawerContext } from 'context/hooks';
import { ApiTaskResult } from 'models/general';
import { useParameters } from 'hooks/useParameters';
import { useCustomMutation } from '../useCustomMutation';

export const getTriggerData = (data: RuleDataForm) => {
  if (data.trigger === RuleTriggers.heartRhythmMeasurement) {
    const { includeBadQuality, ...conditions } = data.data.heartRhythmMeasurement;
    const enabledConditions = Object.values(conditions)
      .filter((condition) => condition.enabled)
      .map((value) => {
        return omit(value, ['enabled']);
      });

    return Object.assign({}, ...enabledConditions, { includeBadQuality });
  }
  return get(data.data, camelCase(data.trigger));
};

const createRule = async (data: RuleDataForm) => {
  if (!data.groupId) throw new EmptyParametersError();

  const triggerData = getTriggerData(data);

  const postData = {
    type: data.trigger,
    notification: {
      title: data.name,
      description: data.description,
      priority: Number(data.priority),
    },
    parameters: {
      frequency: data.frequency,
      ...triggerData,
    },
    targets: data.patientId ? { patientIds: [data.patientId] } : { groupIds: [data.groupId] },
    recipients: { groupIds: [data.groupId] },
  };

  const result = await sdk.tasks.api.post<RuleItem, unknown>(
    API_FUNCTIONS.monitoring,
    RULES_ENDPOINTS.monitoringTemplates,
    postData,
    {},
  );

  const checkResult = checkContainsId({ result });

  if (checkResult && data.toStatus === RuleStatuses.inReview) {
    // after creation put rule in-review
    const resultSubmit = await sdk.tasks.api.post<ApiTaskResult, unknown>(
      API_FUNCTIONS.monitoring,
      `${RULES_ENDPOINTS.monitoringRules}/${result.id}/${RuleTransitions.submit}`,
      {},
      {},
    );
    checkIfGreatSuccess({ result: resultSubmit });
  }

  return result;
};

interface CreateRuleProps {
  reset: () => void;
}

export const useCreateRule = ({ reset }: CreateRuleProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const queryClient = useQueryClient();
  const { parameters: filters, setParameters } = useParameters();
  return useCustomMutation({
    fn: createRule,
    onSuccess: {
      message: t('success_data_created', { value: t('alerts_rule_label') }),
      callback: async (result, variables) => {
        if (variables.toStatus === RuleStatuses.inReview) {
          setParameters({ ...filters, detailId: result.id });
        } else {
          closeDrawer();
        }
        const { groupId, patientId } = variables;

        const queryKey = patientId
          ? [`${queryKeys.patientRules}-${patientId}-${groupId}`]
          : [`${queryKeys.groupRules}-${groupId}`];

        await queryClient.invalidateQueries({ queryKey });
      },
    },
    onError: {
      reset,
    },
  });
};
